import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FolderIcon as Folder,
  ChatCircleIcon as ChatCircle,
  FileTextIcon as FileText,
} from "@phosphor-icons/react";
import { getWorkspaceByShareToken } from "@/lib/api";
import Markdown from "@/components/Markdown";

const API = process.env.REACT_APP_BACKEND_URL || "";

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el && value != null) el.setAttribute(attr, value);
}

export default function SharedWorkspace() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [activeDeliverable, setActiveDeliverable] = useState(null);

  useEffect(() => {
    getWorkspaceByShareToken(token)
      .then(setData)
      .catch(() => setErr("This shared workspace link is invalid or has been revoked."));
  }, [token]);

  // Runtime OG meta + browser title updates so the tab, native share sheets,
  // and JS-rendering preview tools see workspace-specific context. Note: the
  // standard social crawlers (Slack, LinkedIn, X, Discord) fetch HTML server-side
  // and won't see these — they still get the static hero from index.html. Add a
  // proper SSR/edge prerender for /share/{token} to make that crawler-visible too.
  useEffect(() => {
    if (!data) return;
    const { workspace, deliverables } = data;
    const readyCount = (deliverables || []).length;
    const title = `${workspace.name} · Modernization Lab`;
    const desc = workspace.client_name
      ? `Shared workspace for ${workspace.client_name} — ${readyCount} deliverable${readyCount === 1 ? "" : "s"} ready.`
      : `Shared Modernization Lab workspace — ${readyCount} deliverable${readyCount === 1 ? "" : "s"} ready.`;
    const ogImage = `${API}/api/workspaces/share/${token}/og.png`;

    const prevTitle = document.title;
    document.title = title;
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[property="og:image:alt"]', "content", title);
    setMeta('meta[name="twitter:image"]', "content", ogImage);

    return () => {
      // Restore the static hero on unmount so other routes don't inherit the
      // workspace-scoped meta.
      document.title = prevTitle;
    };
  }, [data, token]);

  if (err) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-20 text-center">
        <div className="ml-card p-10">
          <div className="ml-eyebrow mb-2">Shared Workspace</div>
          <h1 className="text-2xl font-semibold mb-3">Link unavailable</h1>
          <p className="text-[var(--ml-text-secondary)]">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 py-20 text-center text-[var(--ml-text-secondary)]">
        Loading shared workspace…
      </div>
    );
  }

  const { workspace, deliverables } = data;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10" data-testid="shared-workspace-page">
      <div className="border-b border-[var(--ml-border)] pb-6 mb-8 flex items-start gap-4">
        <div className="w-12 h-12 bg-[var(--ml-primary)] flex items-center justify-center shrink-0">
          <Folder size={24} weight="bold" color="#fff" />
        </div>
        <div className="flex-1">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
            Shared Workspace · Read-only
          </div>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">{workspace.name}</h1>
          {workspace.client_name && (
            <div className="text-[var(--ml-text-secondary)] mt-1">{workspace.client_name}</div>
          )}
          {workspace.description && (
            <p className="text-sm mt-3 max-w-2xl leading-relaxed">{workspace.description}</p>
          )}
        </div>
      </div>

      <div className="ml-eyebrow mb-3 flex items-center gap-2">
        <FileText size={14} /> Deliverables ({deliverables.length})
      </div>
      {deliverables.length === 0 ? (
        <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]">
          No published deliverables in this workspace yet.
        </div>
      ) : activeDeliverable ? (
        <div>
          <button onClick={() => setActiveDeliverable(null)} className="ml-btn-ghost mb-4">
            ← All deliverables
          </button>
          <div className="ml-card p-6 lg:p-8">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
              {activeDeliverable.deliverable_type.replace(/_/g, " ")}
            </div>
            <h2 className="text-2xl font-semibold tracking-tight mb-6">{activeDeliverable.title}</h2>
            <Markdown>{activeDeliverable.content}</Markdown>
          </div>
        </div>
      ) : (
        <div className="ml-card divide-y divide-[var(--ml-border)]">
          {deliverables.map((d) => (
            <button
              key={d.id}
              onClick={() => setActiveDeliverable(d)}
              className="w-full text-left p-4 lg:p-5 flex items-center justify-between gap-4 hover:bg-[var(--ml-surface)]/50"
              data-testid={`shared-deliv-${d.id}`}
            >
              <div className="min-w-0">
                <div className="font-medium truncate">{d.title}</div>
                <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                  {d.deliverable_type.replace(/_/g, " ")} · {new Date(d.created_at).toLocaleString()}
                </div>
              </div>
              <ChatCircle size={14} color="#0f62fe" weight="bold" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
